import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Color, formatColorString, getPosition, getPositionFromFen, makeMove, } from "@chessalpha/core";
import { InteractiveBoard } from "@chessalpha/react";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Menu, MenuItem, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { CardTitle, theme, useQueryError, useTrack } from "@ssf/components";
import { uciToMove, z } from "@ssf/play-common";
import { createClient } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import { MenuDown, Play, Puzzle as PuzzleIcon } from "mdi-material-ui";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "react-use";
const levels = ["easy", "medium", "hard"];
const size = {
    easy: 396914,
    medium: 604776,
    hard: 1968258,
};
const SUPABASE_URL = "https://mzlfefzpempwjhpsrsnx.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im16bGZlZnpwZW1wd2pocHNyc254Iiwicm9sZSI6ImFub24iLCJpYXQiOjE2NjkxMzE5MzEsImV4cCI6MTk4NDcwNzkzMX0.WdKkRllMXf4asO71qFxgPV1RGg2BUcbldtxN92HTXTo";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
const emptyPosition = getPosition("empty");
const puzzleSchema = z.object({
    id: z.string(),
    fen: z.string(),
    moves: z.string(),
});
const getPuzzle = async (level, offset = 0) => await supabase
    .from(`${level}Puzzles`)
    .select("puzzles(id,fen,moves)")
    .gte("index", offset)
    .limit(1)
    .order("index", { ascending: true })
    .single()
    .then(({ data }) => {
    if (!data) {
        return;
    }
    const puzzle = puzzleSchema.parse(data.puzzles);
    let position = getPositionFromFen(puzzle.fen);
    return {
        id: puzzle.id,
        position: getPositionFromFen(puzzle.fen),
        moves: [
            undefined,
            ...puzzle.moves.split(" ").map((uci) => {
                const move = uciToMove(position, uci);
                position = makeMove(position, move);
                return move;
            }),
        ],
    };
});
const daysSinceStart = Math.ceil((Date.now() - new Date("2022-11-22").getTime()) / (1000 * 3600 * 24));
function getRandomValue(value) {
    return value[Math.floor(Math.random() * value.length)];
}
export const PuzzleCard = () => {
    const { t } = useTranslation("PuzzleCard");
    const isSm = useMediaQuery(theme.breakpoints.up("sm"));
    const isMd = useMediaQuery(theme.breakpoints.up("md"));
    const { width: windowWidth } = useWindowSize();
    const [currentLevel, setLevel] = useState(levels[0]);
    const [levelMenuEl, setLevelMenuEl] = useState();
    const [index, setIndex] = useState(0);
    const [position, setPosition] = useState(emptyPosition);
    const [moves, setMoves] = useState([]);
    const [failure, setFailure] = useState(0);
    const { isLoading, data, error } = useQuery({
        queryKey: ["usePuzzle", currentLevel, index],
        queryFn: () => getPuzzle(currentLevel, index
            ? Math.floor(Math.random() * size[currentLevel]) + 10000
            : daysSinceStart),
        placeholderData: (prev) => prev,
    });
    useQueryError(error);
    useEffect(() => {
        if (data) {
            setPosition(data.position);
            setMoves(data.moves);
            setFailure(0);
        }
    }, [data]);
    useEffect(() => {
        if (moves.length > 1 && moves.length % 2 !== 0) {
            const timeoutId = setTimeout(() => {
                const newMoves = moves.slice(1);
                setPosition((position) => makeMove(position, newMoves[0] ?? 0));
                setMoves(newMoves);
            }, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [moves]);
    const puzzleText = useMemo(() => {
        return getRandomValue(t(`puzzle.${formatColorString((data?.position.toMove ?? 0) ^ 24)}`, {
            returnObjects: true,
        }));
    }, [data, t]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: Observer
    const failText = useMemo(() => {
        return getRandomValue(t("fail", { returnObjects: true }));
    }, [failure, t]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: Observer
    const successText = useMemo(() => {
        return getRandomValue(t("success", { returnObjects: true }));
    }, [data, t]);
    const text = moves.length < 2 ? successText : failure ? failText : puzzleText;
    const { posthog } = useTrack();
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }, children: [_jsx(CardTitle, { title: t("title"), icon: _jsx(PuzzleIcon, {}) }), _jsxs(Box, { children: [_jsx(LoadingButton, { variant: "contained", size: "small", endIcon: _jsx(MenuDown, {}), loading: isLoading, onClick: (event) => setLevelMenuEl(event.currentTarget), children: t(`level.${currentLevel}`) }), _jsx(Menu, { open: Boolean(levelMenuEl), anchorEl: levelMenuEl, onClose: () => setLevelMenuEl(undefined), children: levels.map((level) => (_jsx(MenuItem, { dense: true, selected: level === currentLevel, onClick: () => {
                                        setLevel(level);
                                        setLevelMenuEl(undefined);
                                    }, children: t(`level.${level}`) }, level))) })] })] }), _jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(InteractiveBoard, { position: position, move: moves[0], flipped: data?.position.toMove === Color.white, userColor: (data?.position.toMove ?? 0) ^ 24, size: isMd
                        ? 409
                        : isSm
                            ? theme.breakpoints.values.sm - 8 * 3 * 2
                            : windowWidth - 8 * 3 * 2, backgroundProps: {
                        css: [
                            {
                                "> svg": {
                                    borderRadius: "0.15em",
                                    boxShadow: useTheme().shadows[1],
                                },
                            },
                        ],
                    }, onMove: ({ position, move }) => {
                        const success = move === moves[1];
                        const type = success
                            ? moves.length === 2
                                ? "finished"
                                : "succees"
                            : "failure";
                        posthog?.capture("Puzzle", {
                            puzzleId: data?.id,
                            move,
                            type,
                            level: currentLevel,
                        });
                        if (success) {
                            setPosition(position);
                            setMoves(moves.slice(1));
                            setFailure(0);
                        }
                        else {
                            setFailure((failure) => failure + 1);
                        }
                    } }) }), _jsxs(Box, { sx: { mt: 1, position: "relative" }, children: [data && moves.length < 2 && (_jsx(Button, { variant: "contained", endIcon: _jsx(Play, {}), onClick: () => setIndex((index) => index + 1), sx: { position: "absolute", right: 0 }, children: t("next") })), _jsx(Typography, { variant: "h4", align: "center", sx: { pt: 1 }, children: text })] })] }));
};
//# sourceMappingURL=PuzzleCard.js.map